import { Navigate } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { usePermissions } from '../hooks/usePermissions'

/**
 * Protege rutas según el rol del usuario (alineado con src/config/permissions.js).
 * @param {Object} props
 * @param {React.ReactNode} props.children - Componente a renderizar si tiene acceso
 * @param {Array<string>} props.allowedRoles - Roles permitidos para acceder a la ruta
 */
export default function ProtectedRoute({ children, allowedRoles = [] }) {
  const { user, loading } = useAuth()
  const { role } = usePermissions()
  const [roleWaitExpired, setRoleWaitExpired] = useState(false)

  useEffect(() => {
    setRoleWaitExpired(false)
    if (!user || role || allowedRoles.length === 0) return undefined
    const timer = setTimeout(() => setRoleWaitExpired(true), 2500)
    return () => clearTimeout(timer)
  }, [user, role, allowedRoles.length])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando...</p>
        </div>
      </div>
    )
  }

  if (!user) {
    return <Navigate to="/login" replace />
  }

  if (allowedRoles.length === 0) {
    return <>{children}</>
  }

  // Usuario autenticado pero rol aún no hidratado: esperar breve, no redirigir al instante
  if (!role) {
    if (roleWaitExpired) {
      return <Navigate to="/" replace />
    }
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
          <p className="mt-4 text-gray-600 dark:text-gray-400">Cargando permisos...</p>
        </div>
      </div>
    )
  }

  if (!allowedRoles.includes(role)) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
